import { weekDaysObject } from "@data/constants/activitiesConstants"
import moment from "moment"

/**
 * Slot-string helpers for « Ma dispo ». Every slot the API speaks is the flat
 * "Day HH:MM-HH:MM" form ("Monday 17:00-19:00"); these are the single place that
 * parse it, so the screen and the slot card never re-split it by hand.
 */

/**
 * The week freshness states the backend ships (`verified` / `to_confirm` /
 * `unverified`, mirroring TutorAvailability::STATE_*). Single source of truth on
 * the front so every reader (Ma dispo week view, the monthly reminder and the
 * Offres de cours banner off `profile.availability_freshness`) tests the same
 * literals.
 */
export const AVAILABILITY_FRESHNESS = {
    VERIFIED: "verified",
    TO_CONFIRM: "to_confirm",
    UNVERIFIED: "unverified",
}

/**
 * The nudge verdict off the Redux tree — the single reader for every in-app
 * surface (the monthly reminder, the « Offres de cours » banner).
 *
 * WHO gets nudged is read from the server's own verdict (`update_availability`,
 * i.e. `TutorTrait::getUpdateAvailabilityTask()`), never recomputed here. It used
 * to be mirrored condition for condition off `complete_profile.required`, which
 * held while the two rules matched — until the server stopped standing down for
 * the seasonal « re-declare your levels » task, at which point the home
 * `TutorTaskBanner` (server verdict) and these two surfaces (client mirror) began
 * addressing different tutors during the rentrée window. One owner, one rule.
 *
 * Freshness is still read locally on top: confirming the week updates
 * `currentUser` immediately, while the tasks payload only refreshes on the next
 * `getTutorTasks`, and a banner that lingers after a confirmation reads as a
 * confirmation that did not take.
 *
 * Withheld until the tutor TASKS have landed: the slice is null until
 * `getTutorTasks` resolves, which on a cold start (or a fresh install, where the
 * persisted store holds nothing) happens AFTER the home tab first renders. A nudge
 * deferred by one round-trip costs nothing; a monthly popup shown once burns the
 * month.
 */
export const availabilityNudgeFromState = (state) =>
    state.data.tutors?.tasks?.update_availability?.required === true &&
    state.data.currentUser?.object?.profile?.availability_freshness !==
        AVAILABILITY_FRESHNESS.VERIFIED

/** A date de démarrage behind us (or missing): nothing future can stand on it. */
const isStaleStartingDate = (startingDate) =>
    !startingDate || moment(startingDate).isBefore(moment().startOf("day"))

/**
 * The date the picker OPENS on: the saved one, or TODAY once that one is behind us.
 *
 * The picker only offers [today, today + 1 year], mirroring the server bounds — so
 * a date de démarrage left over from a past season opened the calendar on ITS
 * month, where every day is disabled and keyboard entry is off. The tutor read a
 * stale date, found nothing selectable, and reported the date as stuck on it —
 * « impossible de rajouter mes disponibilités ». Today is the nearest date they
 * can actually pick, so that is where the calendar lands and what the save button
 * confirms in one tap.
 *
 * Opening the picker WRITES nothing: step ① keeps naming the stored date until the
 * tutor confirms another. What retires a stale floor is « Confirmer ma dispo », on
 * the server ({@see \App\Services\Tutor\TutorAvailability::floorUndatedOfferToToday()}).
 */
export const startingDatePickerValue = (startingDate) =>
    isStaleStartingDate(startingDate)
        ? moment().format("YYYY-MM-DD")
        : startingDate

/** "Monday 17:00-19:00" → { day: "Monday", time: "17:00-19:00" }. */
const parseFlatSlot = (slotString) => {
    const [day, ...rest] = slotString.split(" ")
    return { day, time: rest.join(" ") }
}

/** "Monday 17:00-19:00" → "Monday" — the weekday half, English as the API speaks it. */
export const slotDayOf = (slotString) => parseFlatSlot(slotString).day

/**
 * "Monday 17:00-19:00" → "Monday 17:00" — the start, VERBATIM. This is what the
 * save posts: the backend matches it to the stored créneau and keeps that créneau's
 * own form, so a legacy "Saturday 9" is never silently rewritten.
 */
export const slotStartOf = (slotString) => {
    const { day, time } = parseFlatSlot(slotString)
    return `${day} ${time.split("-")[0]}`
}

/**
 * The créneau's IDENTITY — its start with the hour zero-padded ("Saturday 9" →
 * "Saturday 09:00"). Mirrors `TutorAvailability::slotKey()`.
 *
 * Every membership test goes through here rather than through `slotStartOf`. The
 * semaine-type still carries legacy bare-hour and non-padded starts, while the
 * backend grid and the engaged créneaux are always padded — comparing raw strings
 * offered a slot the tutor already had, and the save then stored the same hour
 * twice.
 */
export const slotKeyOf = (slotString) => {
    const { day, time } = parseFlatSlot(slotString)
    const [hours, minutes] = time.split("-")[0].split(":")
    return `${day} ${String(hours).padStart(2, "0")}:${minutes ?? "00"}`
}

// The régime scolaire grid is one-hour by construction (`config/availability.php`
// declares only "HH:00-HH+1:00" ranges), so a slot stored as a bare start implies
// the hour that follows it.
const SLOT_DURATION_HOURS = 1

/** "17:00" → "17h" ; "08:30" → "8h30" (French clock, no dead ":00"). */
export const frTime = (hhmm) => {
    const [hours, minutes] = hhmm.split(":")
    const hour = String(Number(hours))
    return minutes && minutes !== "00" ? `${hour}h${minutes}` : `${hour}h`
}

const plusHours = (hhmm, hours) => {
    const [h, m] = hhmm.split(":")
    const shifted = String((Number(h) + hours) % 24).padStart(2, "0")
    return `${shifted}:${m ?? "00"}`
}

/**
 * "17:00" → "17h-18h" ; "17:00-19:00" → "17h-19h".
 *
 * A créneau is an HOUR the tutor commits to, not a start time: naming the end is
 * what tells them they are free again after it. A slot already stored as a range
 * keeps its own end rather than assuming the grid step.
 */
export const timeRangeLabel = (time) => {
    const [start, end] = time.split("-")
    return `${frTime(start)}-${frTime(end ?? plusHours(start, SLOT_DURATION_HOURS))}`
}

/** "Monday 17:00" → "17h-18h" (the time part of a slot). */
export const slotRangeLabel = (slotString) =>
    timeRangeLabel(parseFlatSlot(slotString).time)

/**
 * The live start times sitting on an engaged créneau, as a French clause:
 * `[]` → "" ; `["18:30"]` → " à 18h30" ; `["18:00","18:30"]` → " à 18h et 18h30".
 *
 * The backend folds a live onto the grid hour that CONTAINS it (`live_times` on
 * each slot of the week view), because that hour is the cell this grid can draw.
 * This is the other half: what the tutor is told is inside the cell. Naming an
 * 18h30 live « 18h » would contradict the one thing they know without looking.
 *
 * Leading space and all, so a caller concatenates it without testing for empty.
 */
export const liveTimesLabel = (times) => {
    if (!times?.length) return ""

    const labels = times.map(frTime)
    const last = labels.pop()

    return labels.length ? ` à ${labels.join(", ")} et ${last}` : ` à ${last}`
}

/**
 * What an hour's chip SAYS, given the créneau's own range label and the lives on
 * it — the rule, extracted from the chip so it can be read on its own.
 *
 * An engaged hour is named by its LIVE, not by the cell it is drawn in: a live at
 * 18h30 sits in the 18h-19h cell because the grid has no 18h30 one, and labelling
 * it « 18h-19h » would tell the tutor their live is at 18h — the one fact they
 * know by heart, contradicted.
 *
 * The range wins in the three cases where the live time adds nothing or does not
 * fit: an OPEN hour, a live already ON the hour (the range says it), and SEVERAL
 * lives in one hour (« 18h · 18h30 » overflows a chip — the dialog and the a11y
 * label carry those). An old API response with no `live_times` lands here too.
 *
 * And in a fourth: when the live's start is ALREADY another chip of that day. The
 * fold does not collapse the off-grid starts the semaine-type still carries, so a
 * week declaring « Thursday 18:30 » next to a live at 18h30 keeps both — the
 * declared créneau on its own chip, the live on the 18h cell that contains it.
 * Renaming that cell « 18h30 » would sit two chips reading 18h30 side by side,
 * one locked and one not, with nothing to tell them apart. The grid hour is what
 * distinguishes them; the dialogue and the a11y label still name the live.
 *
 * @param {{engaged: boolean, time: string, label: string, liveTimes?: string[]}} hour
 * @param {string[]} dayTimes  the time keys of every chip of that day
 */
export const chipLabelOf = (
    { engaged, time, label, liveTimes },
    dayTimes = []
) =>
    engaged &&
    liveTimes?.length === 1 &&
    liveTimes[0] !== time &&
    !dayTimes.includes(liveTimes[0])
        ? frTime(liveTimes[0])
        : label

/** "Monday 17:00" → "Lundi 17h-18h" (French day + the hour it covers). */
export const slotFullLabel = (slotString) => {
    const { day, time } = parseFlatSlot(slotString)
    return `${weekDaysObject[day] ?? day} ${timeRangeLabel(time)}`
}
